// Number-to-word translation for ages on birthday/anniversary templates.
// Covers 1..100 in Uzbek (Latin), English, Russian. Beyond 100 we fall back
// to the numeric form so the UI never shows a broken word.
//
// Used by variant centerpieces where the design originally hardcoded a
// localized word ("THIRTY", "FIFTY", etc) — we now derive it from the
// parsed `ageNumber` so e.g. "Sherzod 21 yosh" renders "TURNS TWENTY-ONE"
// instead of the mock's "TURNS THIRTY".

export type NumLocale = "uz" | "en" | "ru";

const EN_ONES = [
  "zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine",
  "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen",
  "seventeen", "eighteen", "nineteen",
];
const EN_TENS = [
  "", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety",
];

const UZ_ONES = [
  "nol", "bir", "ikki", "uch", "to'rt", "besh", "olti", "yetti", "sakkiz", "to'qqiz",
  "o'n", "o'n bir", "o'n ikki", "o'n uch", "o'n to'rt", "o'n besh",
  "o'n olti", "o'n yetti", "o'n sakkiz", "o'n to'qqiz",
];
const UZ_TENS = [
  "", "", "yigirma", "o'ttiz", "qirq", "ellik", "oltmish", "yetmish", "sakson", "to'qson",
];

const RU_ONES = [
  "ноль", "один", "два", "три", "четыре", "пять", "шесть", "семь", "восемь", "девять",
  "десять", "одиннадцать", "двенадцать", "тринадцать", "четырнадцать", "пятнадцать",
  "шестнадцать", "семнадцать", "восемнадцать", "девятнадцать",
];
const RU_TENS = [
  "", "", "двадцать", "тридцать", "сорок", "пятьдесят", "шестьдесят", "семьдесят",
  "восемьдесят", "девяносто",
];

function numToWordEn(n: number): string {
  if (n < 0 || n > 100) return String(n);
  if (n < 20) return EN_ONES[n];
  if (n === 100) return "one hundred";
  const tens = Math.floor(n / 10);
  const ones = n % 10;
  return ones === 0 ? EN_TENS[tens] : `${EN_TENS[tens]}-${EN_ONES[ones]}`;
}

function numToWordUz(n: number): string {
  if (n < 0 || n > 100) return String(n);
  if (n < 20) return UZ_ONES[n];
  if (n === 100) return "yuz";
  const tens = Math.floor(n / 10);
  const ones = n % 10;
  return ones === 0 ? UZ_TENS[tens] : `${UZ_TENS[tens]} ${UZ_ONES[ones]}`;
}

function numToWordRu(n: number): string {
  if (n < 0 || n > 100) return String(n);
  if (n < 20) return RU_ONES[n];
  if (n === 100) return "сто";
  const tens = Math.floor(n / 10);
  const ones = n % 10;
  return ones === 0 ? RU_TENS[tens] : `${RU_TENS[tens]} ${RU_ONES[ones]}`;
}

export function numToWord(n: number, locale: NumLocale = "uz"): string {
  if (!Number.isFinite(n)) return "";
  const v = Math.floor(n);
  switch (locale) {
    case "en": return numToWordEn(v);
    case "ru": return numToWordRu(v);
    default:   return numToWordUz(v);
  }
}
